import React, { type JSX } from "react";
import { action, makeObservable, observable } from "mobx";
import { observer } from "mobx-react";

import { ModalButtonType, type IModalDialogContent, type ModalDialogOptions, type ModalWindow } from "../Modal";
import type { ChatResponseItemSettings, Model } from "@root/models";

import { PromiseCompletion } from "@classes/PromiseCompletion";
import apiService from "@services/apiService";
import { userService } from "@services/userService";
import ChatToolsTab from "./ChatToolsTab";
import ChatConfigTab from "./ChatConfigTab";
import ChatSkillsTab from "./ChatSkillsTab";

export type ChatConfigDialogProps = {
  chat: ChatResponseItemSettings;
  model: Model | null;
  onSettingsChanged: (settings: ChatResponseItemSettings) => void;
};

@observer
export default class ChatConfigDialog extends React.Component<ChatConfigDialogProps> implements IModalDialogContent<string[] | null> {
  private _loader: PromiseCompletion = new PromiseCompletion();
  @observable private _currentTab: 'chat' | 'tools' | 'skills' = 'chat';
  @observable private _displayedTab: 'chat' | 'tools' | 'skills' = 'chat';
  @observable private _isFadingOut: boolean = false;
  private _transitionTimer: ReturnType<typeof setTimeout> | null = null;
  @observable private _chat: ChatResponseItemSettings;

  constructor(props: ChatConfigDialogProps){
    super(props);
    this._chat = props.chat;
    makeObservable(this);
  }

  componentWillUnmount(): void {
    if (this._transitionTimer) clearTimeout(this._transitionTimer);
  }

  private switchTab(tab: 'chat' | 'tools' | 'skills'): void {
    if (tab === this._currentTab) return;
    this._currentTab = tab;
    if (this._transitionTimer) clearTimeout(this._transitionTimer);
    this._isFadingOut = true;
    this._transitionTimer = setTimeout(action(() => {
      this._displayedTab = tab;
      this._isFadingOut = false;
    }), 150);
  }

  private renderHeaderTabsInTitle(): JSX.Element {
    return (
      <div className="modal-header-tabs">
        <div className={`modal-header-tab ${this._currentTab === 'chat' ? 'modal-header-tab-active' : ''}`} onClick={() => this.switchTab('chat')}>
          Chat{!this._chat?.id ? <span style={{color: 'var(--accent-color)'}}>*</span> : ''}
        </div>
        <div className={`modal-header-tab ${this._currentTab === 'skills' ? 'modal-header-tab-active' : ''}`} onClick={() => this.switchTab('skills')}>
          Skills
        </div>
        <div className={`modal-header-tab ${this._currentTab === 'tools' ? 'modal-header-tab-active' : ''}`} onClick={() => this.switchTab('tools')}>
          Tools
        </div>
        {this._chat.tools && <span className="selected-count">
          {this._chat.tools?.length || 0} selected
        </span>}
      </div>
    );
  }

  public getModalOptions(window: ModalWindow<string[] | null>): ModalDialogOptions<string[] | null> {
    return {
      title: this.renderHeaderTabsInTitle(),
      className: 'modal-top',
      loader: this._loader,
      buttons: [{
        title: 'Save as default',
        type: ModalButtonType.Save,
        onClick: async () => {
          const tools = this._chat?.tools;
          localStorage.setItem('defaultTools', JSON.stringify(tools));
          await apiService.put('/api/user/settings', {
            defaultTools: this._chat.tools,
            defaultTemperature: this._chat.temperature,
            defaultEnableSyntaxHighlighting: this._chat.enableSyntaxHighlighting,
            defaultThinkMode: this._chat.thinkMode,
            defaultShowFullThinking: this._chat.showFullThinking,
            defaultContextLength: this._chat.contextLength,
            defaultWhisperLanguage: this._chat.whisperLanguage,
            defaultModelName: this.props.model ? this.props.model.server + '@' + this.props.model.name : null,
            defaultEnabledSkills: this._chat.enabledSkills ?? [],
          });
          await userService.loadUserSettings();
          
          window.close(ModalButtonType.Save, tools);
        }        
      },{
        title: 'Close',
        type: ModalButtonType.Cancel,
        onClick: () => {
          window.close(ModalButtonType.Cancel);
        }
      }]
    };
  }

  render() {
    const content = this._displayedTab === 'chat'
      ? <ChatConfigTab chat={this.props.chat} onSettingsChanged={this._onSettingsChanged} />
      : this._displayedTab === 'skills'
        ? <ChatSkillsTab enabledSkills={this._chat.enabledSkills ?? []} loader={this._loader} onChange={this._onSkillsChanged} />
        : <ChatToolsTab tools={this._chat.tools} loader={this._loader} onChange={this._onToolsChanged} />;

    return (
      <div className={`tab-content${this._isFadingOut ? ' tab-content--fading-out' : ''}`}>
        {content}
      </div>
    );
  }

  private _onToolsChanged = (tools: string[]) => {
    this._chat.tools = tools;
    this.props.onSettingsChanged(this._chat);
  }

  private _onSkillsChanged = (enabledSkills: string[]) => {
    this._chat.enabledSkills = enabledSkills;
    this.props.onSettingsChanged(this._chat);
  }

  private _onSettingsChanged = (settings: ChatResponseItemSettings) => {
    this._chat = { ...this._chat, ...settings };
    this.props.onSettingsChanged(this._chat);
  }
}